import { useCallback, useEffect, useMemo, FC } from "react";
import { Box, Button } from "@mui/material";
import { ArrowForwardIos } from "@mui/icons-material";
import { NewLearningRequestStep } from "utils/types";
import { useIntroStepStyles } from "./style";
import { useTranslation } from "react-i18next";
import i18n from "utils/i18n";
import { RenderHTML } from "components";

interface NewLearningStepProps {
  onNext: (currentStepParam: NewLearningRequestStep) => void;
  isMobile: boolean;
  courseSelection: boolean | null;
  setCourseSelection: (value: boolean | null) => void;
  setDialogTitle?: (title: string) => void;
}

export const NewLearning: FC<NewLearningStepProps> = ({
  onNext,
  isMobile,
  courseSelection,
  setCourseSelection,
  setDialogTitle,
}) => {
  const { t } = useTranslation();

  const classes = useIntroStepStyles({ isMobile });

  useEffect(() => {
    setDialogTitle?.(t("learning_request"));
  }, [setDialogTitle, t]);

  const isSelectionMade = useMemo(() => courseSelection !== null, [courseSelection]);

  const getButtonClass = (value: boolean) =>
    courseSelection === value ? classes.primaryButton : classes.outlineButton;

  const getButtonVariant = (value: boolean) =>
    courseSelection === value ? "contained" : "outlined";

  const handleNext = useCallback(() => {
    if (isSelectionMade) {
      if (courseSelection) {
        onNext(NewLearningRequestStep.CourseDetails);
      } else {
        onNext(NewLearningRequestStep.LearningAnalysis);
      }
    }
  }, [isSelectionMade, courseSelection, onNext]);

  const handleSelection = useCallback(
    (value: boolean) => {
      setCourseSelection(value);
    },
    [setCourseSelection],
  );

  return (
    <Box className={classes.container}>
      {i18n.exists("new_learning_request_note") &&
        i18n.exists("new_learning_request_note_description") && (
          <Box className={classes.notesContainer}>
            <Box className={`${classes.highlightNote} ${classes.blueNote}`} role="note">
              {t("new_learning_request_note")}
            </Box>
            <Box className={`${classes.highlightNote} ${classes.orangeNote}`} role="note">
              <RenderHTML content={t("new_learning_request_note_description")} />
            </Box>
          </Box>
        )}
      <Box className={classes.descriptionText}>{t("course_selection_title_question")}</Box>

      <Box className={classes.contentCard}>
        <Box>
          <Box className={classes.sectionTitle}>{t("course_selection")}</Box>

          <Box>
            <Box className={classes.sectionQuestion}>{t("course_selection_question")}</Box>
            <Box
              className={classes.buttonContainer}
              role="group"
              aria-label={t("course_selection_question")}
            >
              <Button
                type="button"
                onClick={() => handleSelection(true)}
                variant={getButtonVariant(true)}
                className={getButtonClass(true)}
                fullWidth
                aria-pressed={courseSelection === true}
              >
                {t("course_selection_yes")}
              </Button>
              <Button
                type="button"
                onClick={() => handleSelection(false)}
                variant={getButtonVariant(false)}
                className={getButtonClass(false)}
                fullWidth
                aria-pressed={courseSelection === false}
              >
                {t("course_selection_no")}
              </Button>
            </Box>
          </Box>
        </Box>

        {isSelectionMade && (
          <Box className={classes.actionButtonContainer}>
            <Button
              type="button"
              variant="contained"
              onClick={handleNext}
              className={classes.primaryButton}
              endIcon={<ArrowForwardIos />}
            >
              {t("next")}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};
